// Hazır tasarım şemaları.
//
// Yönetim panelindeki Ayarlar → Tasarım sekmesinden seçilir, seçim
// `settings.site_theme` alanında saklanır. Bir şema iki şey belirler:
//   * `tokens` — zemin, kart, kenarlık, tipografi ve geometri değişkenleri.
//     applySiteTheme() bunları <html> üzerine yazar, App.css'teki :root
//     varsayılanlarının yerine geçer.
//   * `accent` / `surface` — panelde renk alanı boş bırakıldığında kullanılan
//     vurgu ve üst bant renkleri. Yönetici renk seçerse onunki kazanır.
//
// Yeni bir şema eklerken backend `Settings.site_theme` deseni (a-z0-9_-) ile
// uyumlu bir id verin; ayrıca App.css'te html[data-site-theme="<id>"] altına
// değişkenle ifade edilemeyen ince dokunuşları yazabilirsiniz.

export const DEFAULT_THEME_ID = 'betrun';

// `shell` şemanın kabuk geometrisidir ve yalnızca panelin şema önizlemesi
// tarafından okunur: altı kartta da aynı maketi çizmek, şemalar arasındaki
// asıl farkı (menünün nerede durduğunu) gizliyordu — renkten başka bir şey
// değişmiyordu. Yeni bir şema eklerken bu alanı da doldurun, yoksa kart
// klasik düzende çizilir.
export const SITE_THEMES = [
  // ---- Varsayılan şema: Betrun -------------------------------------------
  // Kurulumun kullandığı tasarım varsayılandır (05.10.2026): listede ilk
  // karttır, `DEFAULT_THEME_ID` odur ve tanınmayan şema ona düşer.
  {
    id: 'betrun',
    shell: 'rail-top',
    name: 'Betrun',
    description: 'Varsayılan tema: sol ray + üst bar, siyah zemin, oyun kendi sayfasında açılır.',
    // Oyun seçim penceresi açılmadan başlar: üye gerçek modda, ziyaretçi
    // demosu varsa demoda (bkz. hooks/useGameLauncher.js).
    directLaunch: true,
    // Oyun pencere değil **kendi sayfasında** açılır (`/oyun/:id`): solda oyun,
    // sağda öneri sütunu (bkz. pages/GamePage.jsx, lib/gamePage.js).
    gamePage: true,
    // iOS'ta giriş kutusuna dokununca sayfa yakınlaşmaz: viewport
    // `maximum-scale=1` ile kilitlenir; iki parmakla yakınlaştırma açıktır
    // (bkz. lib/viewportLock.js).
    lockViewport: true,
    accent: '#bc9b6a',
    surface: '#141414',
    // İkinci renk boş bırakılırsa altına değil buradaki siyaha düşülür.
    keepSurface: true,
    swatches: ['#050505', '#141414', '#bc9b6a'],
    tokens: {
      '--pusu-font': "'Inter', sans-serif",
      '--pusu-bg': '#050505',
      '--pusu-bg-deep': '#000000',
      '--pusu-bg-2': '#0c0c0c',
      '--pusu-card': '#101010',
      '--pusu-card-2': '#181818',
      '--pusu-card-3': '#161616',
      '--pusu-card-4': '#0c0c0c',
      '--pusu-surface-hover': '#222222',
      '--pusu-border': '#262626',
      '--pusu-border-strong': '#3a3a3a',
      '--pusu-header-bg': 'rgba(16, 16, 16, 0.97)',
      '--pusu-radius-card': '12px',
      '--pusu-radius-panel': '12px',
      '--pusu-radius-btn': '12px',
      '--pusu-radius-tile': '10px',
      '--pusu-glow-1': '0.1',
      '--pusu-glow-2': '0.03',
    },
  },
  {
    id: 'default',
    shell: 'classic',
    name: 'Klasik',
    // Şemaya geçmek parola ister (sistem sahibi, 23.10.2026); bkz. backend/services/theme_lock.py.
    locked: true,
    description: 'Koyu mor-siyah zemin, tek renk vurgulu klasik görünüm.',
    accent: '#c71a1a',
    surface: '#c71a1a',
    swatches: ['#08060d', '#15111d', '#c71a1a'],
    tokens: {
      '--pusu-font': "'Poppins', sans-serif",
      '--pusu-bg': '#08060d',
      '--pusu-bg-deep': '#0b0812',
      '--pusu-bg-2': '#100c18',
      '--pusu-card': '#15111d',
      '--pusu-card-2': '#1c1728',
      '--pusu-card-3': '#171223',
      '--pusu-card-4': '#110d1a',
      '--pusu-surface-hover': '#191325',
      '--pusu-border': '#2b2340',
      '--pusu-border-strong': '#3d3358',
      '--pusu-header-bg': 'rgba(12, 8, 20, 0.9)',
      '--pusu-radius-card': '14px',
      '--pusu-radius-panel': '16px',
      '--pusu-radius-btn': '12px',
      '--pusu-radius-tile': '13px',
      '--pusu-glow-1': '0.55',
      '--pusu-glow-2': '0.07',
    },
  },
  {
    // Nova'nın yerini aldı (18.10.2026). Kayıtta `nova` ya da `merit` duran
    // kurulum bu şemayla açılır (THEME_ALIASES). Görsel dil bir BetConstruct
    // referans arayüzünden alındı (scripts/asus-build.js); referansın markası,
    // kampanyaları ve iletişim bilgisi taşınmadı — logo ve içerik CMS'ten gelir.
    id: 'asus',
    shell: 'bc-top',
    name: 'Asus',
    // Şemaya geçmek parola ister (sistem sahibi, 23.10.2026); bkz. backend/services/theme_lock.py.
    locked: true,
    description: 'Siyah zemin, altın vurgu, iki katlı üst menü ve pencerede açılan hesap paneliyle BetConstruct düzeni.',
    // Oyun kendi sayfasında açılır (`/oyun/:id`), seçim penceresi yoktur.
    directLaunch: true,
    gamePage: true,
    accent: '#c5a037',
    surface: '#090808',
    keepSurface: true,
    // Referans paletinin altın + siyah dengesi; panelden gelen vurgu şemayı
    // tanınmaz yapar.
    fixedColors: true,
    swatches: ['#090808', '#141212', '#c5a037'],
    tokens: {
      '--pusu-font': "'Roboto', Arial, Helvetica, sans-serif",
      '--pusu-bg': '#090808',
      '--pusu-bg-deep': '#050404',
      '--pusu-bg-2': '#100e0e',
      '--pusu-card': '#141212',
      '--pusu-card-2': '#1b1818',
      '--pusu-card-3': '#171414',
      '--pusu-card-4': '#100e0e',
      '--pusu-surface-hover': '#242020',
      '--pusu-border': '#2a2626',
      '--pusu-border-strong': '#3b3535',
      '--pusu-header-bg': '#090808',
      '--pusu-radius-card': '4px',
      '--pusu-radius-panel': '4px',
      '--pusu-radius-btn': '4px',
      '--pusu-radius-tile': '4px',
      '--pusu-glow-1': '0.06',
      '--pusu-glow-2': '0.02',
    },
  },
  {
    id: 'zumrut',
    shell: 'wide-left',
    name: 'Zümrüt',
    description: 'Koyu zümrüt zemin, altın vurgu ve geniş başlıklı sol menüsüyle klasik bahis sitesi görünümü.',
    // Şemaya geçmek parola ister; parolanın kendisi yalnızca backend'de
    // (özet olarak) durur, bkz. backend/services/theme_lock.py. Buradaki
    // bayrak sadece panelin parola penceresini açması içindir.
    locked: true,
    // Vurgu altın, yüzey zümrüt: kabuğun gradyanları (başlık, sol menü, footer)
    // ikinci renkten türer, bu yüzden ikisi de panelden değiştirilebilir.
    accent: '#ffcf73',
    surface: '#00554d',
    // İkinci renk alanı boş bırakılırsa ana renge değil buradaki zümrüde
    // düşülür; aksi hâlde kabuk altın rengine boyanırdı (bkz. lib/theme.js).
    keepSurface: true,
    swatches: ['#001210', '#00241f', '#ffcf73'],
    tokens: {
      '--pusu-font': "'Jost', sans-serif",
      '--pusu-bg': '#001210',
      '--pusu-bg-deep': '#000908',
      '--pusu-bg-2': '#001b18',
      '--pusu-card': '#00241f',
      '--pusu-card-2': '#003029',
      '--pusu-card-3': '#002b25',
      '--pusu-card-4': '#001815',
      '--pusu-surface-hover': '#013c35',
      '--pusu-border': '#0b3d36',
      '--pusu-border-strong': '#14615a',
      '--pusu-header-bg': 'rgba(0, 33, 29, 0.96)',
      // Referans arayüzde düğme ve rozetler hap, kart ve paneller yumuşak köşeli.
      '--pusu-radius-card': '14px',
      '--pusu-radius-panel': '14px',
      '--pusu-radius-btn': '999px',
      '--pusu-radius-tile': '10px',
      '--pusu-glow-1': '0.2',
      '--pusu-glow-2': '0.05',
    },
  },
  {
    id: 'paris',
    shell: 'two-tier',
    name: 'Paris',
    description: 'Antrasit-lacivert zemin, kırmızı vurgu ve iki katlı üst menüsüyle klasik bahis portalı görünümü.',
    // Şemaya geçmek parola ister; parolanın kendisi yalnızca backend'de
    // (özet olarak) durur, bkz. backend/services/theme_lock.py. Buradaki
    // bayrak sadece panelin parola penceresini açması içindir.
    locked: true,
    // Vurgu kırmızı, yüzey antrasit-lacivert: başlık, üst menü ve footer
    // gradyanları ikinci renkten türer, bu yüzden ikisi de panelden
    // değiştirilebilir.
    accent: '#bd1d25',
    surface: '#292f41',
    // İkinci renk boş bırakılırsa ana renge (kırmızıya) değil buradaki
    // antrasite düşülür; aksi hâlde başlık ve üst menü tamamen kırmızıya
    // boyanırdı (bkz. lib/theme.js `keepSurface`).
    keepSurface: true,
    swatches: ['#171723', '#292f41', '#bd1d25'],
    tokens: {
      '--pusu-font': "'Rubik', sans-serif",
      '--pusu-bg': '#171723',
      '--pusu-bg-deep': '#12121d',
      '--pusu-bg-2': '#1b1f2a',
      '--pusu-card': '#232733',
      '--pusu-card-2': '#2b2e3a',
      '--pusu-card-3': '#292b34',
      '--pusu-card-4': '#1e202a',
      '--pusu-surface-hover': '#353741',
      '--pusu-border': '#333545',
      '--pusu-border-strong': '#42444f',
      '--pusu-header-bg': 'rgba(30, 33, 48, 0.97)',
      // Referans arayüzde köşeler klasik ve dardır: düğmeler 6px, kartlar
      // 12px. Hap geometri (999px) bu şemada hiç kullanılmaz.
      '--pusu-radius-card': '12px',
      '--pusu-radius-panel': '12px',
      '--pusu-radius-btn': '6px',
      '--pusu-radius-tile': '10px',
      '--pusu-glow-1': '0.18',
      '--pusu-glow-2': '0.05',
    },
  },
  {
    id: 'herkul',
    shell: 'three-col',
    name: 'Herkül',
    description: 'Teal-siyah zemin, altın vurgu ve sol ikon rayıyla premium casino görünümü.',
    // Şemaya geçmek parola ister; parolanın kendisi yalnızca backend'de
    // (özet olarak) durur, bkz. backend/services/theme_lock.py. Buradaki
    // bayrak sadece panelin parola penceresini açması içindir.
    locked: true,
    // Vurgu altın, yüzey teal: kabuğun gradyanları (başlık, sol ray, footer)
    // ikinci renkten türer, bu yüzden ikisi de panelden değiştirilebilir.
    accent: '#e4bb6c',
    surface: '#05484d',
    // İkinci renk boş bırakılırsa ana renge (altına) değil buradaki teale
    // düşülür; aksi hâlde başlık ve ray tümüyle altına boyanırdı
    // (bkz. lib/theme.js `keepSurface`).
    keepSurface: true,
    swatches: ['#010e0f', '#022123', '#e4bb6c'],
    tokens: {
      '--pusu-font': "'Roboto', sans-serif",
      '--pusu-bg': '#011314',
      '--pusu-bg-deep': '#010e0f',
      '--pusu-bg-2': '#011819',
      '--pusu-card': '#022123',
      '--pusu-card-2': '#032d30',
      '--pusu-card-3': '#022a2c',
      '--pusu-card-4': '#011819',
      '--pusu-surface-hover': '#04393d',
      '--pusu-border': '#032d30',
      '--pusu-border-strong': '#04565b',
      '--pusu-header-bg': 'rgba(1, 14, 15, 0.95)',
      // Referans arayüzde kartlar yumuşak, düğmeler daha dar köşelidir.
      '--pusu-radius-card': '12px',
      '--pusu-radius-panel': '14px',
      '--pusu-radius-btn': '8px',
      '--pusu-radius-tile': '10px',
      // Teal-siyah zemin neredeyse mattır; renkli hale çok düşüktür.
      '--pusu-glow-1': '0.16',
      '--pusu-glow-2': '0.05',
    },
  },
  {
    id: 'vega',
    shell: 'top-offcanvas',
    name: 'Vega',
    description: 'Siyah zemin, altın vurgu, hap düğmeler ve sol offcanvas menüsüyle modern casino görünümü.',
    // Şemaya geçmek parola ister; parolanın kendisi yalnızca backend'de
    // (özet olarak) durur, bkz. backend/services/theme_lock.py. Buradaki
    // bayrak sadece panelin parola penceresini açması içindir.
    locked: true,
    // Vurgu altın, yüzey saf siyah üstü antrasit. **Bu ikisi şemanın kimliğidir
    // ve panelden değiştirilemez** (`fixedColors`): kabuğun bütün gradyanları,
    // hap düğmeleri ve açık görünüm anahtarı altın/siyah dengesine göre
    // ölçüldü; başka bir vurguyla şema tanınmaz hâle geliyordu. Panel bu
    // yüzden Vega seçiliyken renk alanlarını hiç göstermez ve buildSiteTheme
    // kayıtta duran eski renkleri yok sayar.
    accent: '#ba8e47',
    surface: '#161616',
    fixedColors: true,
    // İkinci renk boş bırakılırsa ana renge (altına) değil buradaki antrasite
    // düşülür; aksi hâlde başlık tümüyle altına boyanırdı
    // (bkz. lib/theme.js `keepSurface`).
    keepSurface: true,
    swatches: ['#000000', '#161616', '#ba8e47'],
    tokens: {
      '--pusu-font': "'Inter', sans-serif",
      // Referansın yüzeyleri iki değerdir: sayfa ve kartlar **saf siyah**,
      // başlık ve sekme şeridi bir ton açık gri (#161616). Aradaki her şey
      // bu ikisinden türer; ara tonlar uydurulursa şema "koyu gri" görünür
      // ve referansın sertliğini kaybeder.
      '--pusu-bg': '#000000',
      '--pusu-bg-deep': '#000000',
      '--pusu-bg-2': '#0e0e0e',
      '--pusu-card': '#000000',
      '--pusu-card-2': '#161616',
      '--pusu-card-3': '#1c1c1c',
      '--pusu-card-4': '#0a0a0a',
      '--pusu-surface-hover': '#222222',
      // Çizgiler referansta sabit renk değil **beyazın saydamıdır**: saf
      // siyah zeminde gri bir çizgi ölü durur, saydam beyaz kartın kendi
      // tonuna uyar.
      '--pusu-border': 'rgba(255, 255, 255, 0.1)',
      '--pusu-border-strong': 'rgba(255, 255, 255, 0.2)',
      '--pusu-header-bg': '#161616',
      // Referans arayüzde **düğmeler hap, kartlar dar köşelidir** ve bu ayrım
      // şemanın imzasıdır: 25px yarıçaplı düğmelerin yanında 5px köşeli
      // kartlar durur. İkisini eşitlemek görünümü tanınmaz hâle getirir.
      '--pusu-radius-card': '5px',
      '--pusu-radius-panel': '5px',
      '--pusu-radius-btn': '25px',
      '--pusu-radius-tile': '5px',
      // Antrasit zemin mattır; renkli hale neredeyse yoktur.
      '--pusu-glow-1': '0.12',
      '--pusu-glow-2': '0.04',
    },
  },
  {
    // Spinlab (07.10.2026): spilabtema paketinden gelen ayrı kabuk. Görünüm
    // tamamen kendi stil dosyasından gelir (public/themes/spinlab/spinlab.css,
    // `.spl-root` altına kapsamlı); buradaki tokenlar yalnızca şemanın içinde
    // çizilen ortak sayfaların (promosyon, hesap, spor) Spinlab paletine
    // uyması içindir. Kod ve stil yalnızca bu şema seçiliyken yüklenir
    // (bkz. themes/spinlab/index.jsx, docs/kb/05 *Spinlab şeması*).
    id: 'spinlab',
    shell: 'sidebar-dashboard',
    name: 'Spinlab',
    // Şemaya geçmek parola ister (sistem sahibi, 23.10.2026); bkz. backend/services/theme_lock.py.
    locked: true,
    description: 'Gece laciverti zemin, mor vurgu (Obsidian Violet), daraltılabilir sol menü ve Casino/Spor geçişli modern casino görünümü.',
    // Kaynak temada oyun kartı oyunun sayfasına gider; oyna düğmesi seçim
    // penceresi açmadan başlatır (üye gerçek modda, ziyaretçi demoda).
    directLaunch: true,
    accent: '#7c3aed',
    surface: '#13152c',
    // Renkler şemanın kimliğidir; panelden gelen vurgu kaynak tasarımı bozar.
    fixedColors: true,
    keepSurface: true,
    swatches: ['#0b0f1a', '#13152c', '#7c3aed'],
    tokens: {
      '--pusu-font': "'Alexandria', sans-serif",
      // Spinlab palet bloğunun yüzeyleriyle aynı (overrides.css, 27.10.2026).
      '--pusu-bg': '#0b0f1a',
      '--pusu-bg-deep': '#090c15',
      '--pusu-bg-2': '#0f1127',
      '--pusu-card': '#13152c',
      '--pusu-card-2': '#1b1c33',
      '--pusu-card-3': '#10132a',
      '--pusu-card-4': '#0f1127',
      '--pusu-surface-hover': '#1f1636',
      '--pusu-border': '#2b2a4a',
      '--pusu-border-strong': '#4c357b',
      '--pusu-header-bg': '#0b0c20',
      '--pusu-radius-card': '14px',
      '--pusu-radius-panel': '16px',
      '--pusu-radius-btn': '8px',
      '--pusu-radius-tile': '14px',
      '--pusu-glow-1': '0.08',
      '--pusu-glow-2': '0.03',
    },
  },
];

export const SITE_THEME_IDS = SITE_THEMES.map((theme) => theme.id);

/** Panelin şema listesinde çizilen şemalar (özel şemalar hariç). */
export const LISTED_SITE_THEMES = SITE_THEMES.filter((theme) => !theme.hidden);

/**
 * Panelin *Özel tema* alanına yazılan değeri özel şemaya çözer.
 *
 * Değer düz anahtar (`betrun`) ya da bir konum olabilir
 * (`themes/betrun`, `https://…/betrun`): son yol parçası alınır. Yalnızca
 * `hidden` şemalar bu yoldan açılır — listelenen şemaların yeri kartlardır.
 */
export const findCustomTheme = (value) => {
  const key = String(value || '').trim().toLowerCase()
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .split('/')
    .pop();
  if (!key) return null;
  return SITE_THEMES.find((theme) => theme.hidden && theme.id === key) || null;
};

/** Şema, oyunu seçim penceresi açmadan mı başlatır. */
export const themeLaunchesDirectly = (themeId) => Boolean(
  SITE_THEMES.find((theme) => theme.id === themeId)?.directLaunch,
);

/** Oyunu pencere yerine `/oyun/:id` sayfasında açan şema mı. */
export const themeUsesGamePage = (themeId) => Boolean(
  SITE_THEMES.find((theme) => theme.id === themeId)?.gamePage,
);

// Kaldırılan şemaların yerini alan şemalar: kayıtta eski anahtar duran
// kurulum klasik görünüme düşmez, yerini alan şemayla açılır.
// Kaldırılan şemaların kayıtları: Merit → Nova (04.10.2026) → Asus (18.10.2026).
export const THEME_ALIASES = { merit: 'asus', nova: 'asus' };

export const resolveThemeId = (themeId) => THEME_ALIASES[themeId] || themeId;

export const getSiteTheme = (themeId) => (
  SITE_THEMES.find((theme) => theme.id === resolveThemeId(themeId)) || SITE_THEMES[0]
);

// Parola isteyen şemalar. Kilit yalnızca **geçişte** işler: şemadan çıkmak
// parola istemez, aksi hâlde parolayı kaybeden yönetici siteyi eski
// görünümüne döndüremezdi (backend de aynı kuralı uygular).
export const isThemeLocked = (themeId) => Boolean(
  SITE_THEMES.find((theme) => theme.id === themeId)?.locked,
);

// Renkleri şemanın kendi kimliği olan şemalar (bugün yalnızca Vega). Panel bu
// şemalarda renk alanlarını hiç çizmez, `buildSiteTheme` de ayar kaydındaki
// renkleri yok sayar — ikisi ayrışırsa panel bir şey gösterir, site başka
// şey çizerdi.
export const themeHasFixedColors = (themeId) => Boolean(
  SITE_THEMES.find((theme) => theme.id === themeId)?.fixedColors,
);
